import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { RadioGroup } from 'src/components/RadioGroup';
import { Slider } from 'src/components/Slider';
import { TextField } from 'src/components/TextField';
import { ToggleButton } from 'src/components/ToggleButton';
import { ToggleButtonGroup } from 'src/components/ToggleButtonGroup';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import {
  LeaderboardAgeFilter,
  LeaderboardFiltersState,
} from 'src/models/leaderboard/_types';
import { fetchLocations } from 'src/models/location/location';
import { Tag, fetchTagsByType } from 'src/models/tags/tags';
import { Identifier } from 'src/types/utilityTypes';

import { useLeaderboardFilters } from './useLeaderboardFilters';

interface LeaderboardFiltersModalProps {
  open: boolean;
  onClose: () => void;
  availableFilters: string[];
  ageFilters?: LeaderboardAgeFilter[];
}

interface LocationOption {
  id: Identifier;
  name: string;
}

type LocationMode = 'all' | 'mine' | 'custom';

const EMPTY_FILTERS: LeaderboardFiltersState = {
  locationId: null,
  locationIds: [],
  locationTags: [],
  ageMin: null,
  ageMax: null,
  gender: null,
};

export const LeaderboardFiltersModal = ({
  open,
  onClose,
  availableFilters,
  ageFilters = [],
}: LeaderboardFiltersModalProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.public.filters',
  });
  const { user } = useSession();
  const { filters, setFilters } = useLeaderboardFilters();
  const [localFilters, setLocalFilters] =
    useState<LeaderboardFiltersState>(filters);

  const showLocation = availableFilters.includes('location');

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: () =>
      fetchLocations({ fetchAll: true, sort: 'name', sortDir: 'asc' }),
    staleTime: 60000,
    enabled: showLocation && open,
    select: (data) => data.locations,
  });

  const { data: locationTags } = useQuery({
    queryKey: ['locationTags'],
    queryFn: () => fetchTagsByType('location'),
    staleTime: 60000,
    enabled: showLocation && open,
  });

  const locationOptions: LocationOption[] = (locations || []).map((loc) => ({
    id: loc.id,
    name: loc.name,
  }));

  const selectedLocations = locationOptions.filter((loc) =>
    (localFilters.locationIds || []).map(String).includes(String(loc.id))
  );

  const userLocationId = user.locationId;

  const deriveLocationMode = useCallback(
    (ids: Identifier[]): LocationMode => {
      if (ids.length === 0) return 'all';
      if (ids.length === 1 && String(ids[0]) === String(userLocationId)) {
        return 'mine';
      }
      return 'custom';
    },
    [userLocationId]
  );

  const [locationMode, setLocationMode] = useState<LocationMode>(() =>
    deriveLocationMode(filters.locationIds || [])
  );

  useEffect(() => {
    if (open) {
      setLocalFilters(filters);
      setLocationMode(deriveLocationMode(filters.locationIds || []));
    }
  }, [open, filters, deriveLocationMode]);

  const handleReset = () => {
    setLocalFilters(EMPTY_FILTERS);
    setLocationMode('all');
  };

  const handleApply = () => {
    setFilters(localFilters);
    onClose();
  };

  const handleLocationModeChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    const mode = value as LocationMode;
    setLocationMode(mode);
    if (mode === 'all') {
      setLocalFilters((prev) => ({
        ...prev,
        locationId: null,
        locationIds: [],
      }));
    } else if (mode === 'mine' && user.locationId) {
      setLocalFilters((prev) => ({
        ...prev,
        locationId: user.locationId || null,
        locationIds: [user.locationId!],
      }));
    }
  };

  const selectedLocationTags = (locationTags || []).filter((tag) =>
    (localFilters.locationTags || []).includes(tag.name)
  );

  const handleLocationTagsChange = (selected: Tag[]) => {
    setLocalFilters((prev) => ({
      ...prev,
      locationTags: selected.map((tag) => tag.name),
    }));
  };

  const handleLocationsChange = (selected: LocationOption[]) => {
    const ids = selected.map((loc) => loc.id);
    setLocalFilters((prev) => ({
      ...prev,
      locationIds: ids,
      locationId: ids.length === 1 ? ids[0] : null,
    }));
    setLocationMode(deriveLocationMode(ids));
  };

  const handleGenderChange = (
    _: React.MouseEvent<HTMLElement>,
    value: string | null
  ) => {
    setLocalFilters((prev) => ({
      ...prev,
      gender: value === 'all' ? null : value,
    }));
  };

  const handleAgeChange = (_: Event, value: number | number[]) => {
    if (Array.isArray(value)) {
      setLocalFilters((prev) => ({
        ...prev,
        ageMin: value[0],
        ageMax: value[1],
      }));
    }
  };

  const handleAgeGroupChange = (label: string) => {
    if (!label) {
      setLocalFilters((prev) => ({ ...prev, ageMin: null, ageMax: null }));
      return;
    }
    const found = ageFilters.find((f) => f.label === label);
    if (found) {
      setLocalFilters((prev) => ({
        ...prev,
        ageMin: found.minAge,
        ageMax: found.maxAge,
      }));
    }
  };

  const genderValue = localFilters.gender || 'all';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      title={t('title')}
      rightAdornment={
        <Button size="small" onClick={handleReset}>
          {t('reset')}
        </Button>
      }
      actions={[
        {
          label: t('apply'),
          onClick: handleApply,
          variant: 'prominent',
          fullWidth: true,
        },
      ]}
    >
      {showLocation && (
        <Box sx={{ mb: 3 }}>
          <RadioGroup
            label={t('showResultsFrom')}
            value={locationMode === 'custom' ? '' : locationMode}
            onChange={handleLocationModeChange}
            row
            options={[
              { label: t('allLocations'), value: 'all' },
              { label: t('myLocation'), value: 'mine' },
            ]}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', my: 1 }}
          >
            {t('or')}
          </Typography>
          <Autocomplete<LocationOption, true>
            multiple
            options={locationOptions}
            value={selectedLocations}
            onChange={(_, selected) =>
              handleLocationsChange(selected as LocationOption[])
            }
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id)
            }
            renderInput={(params) => (
              <TextField {...params} label={t('selectLocation')} size="small" />
            )}
          />
          <Box sx={{ mt: 2 }}>
            <Autocomplete<Tag, true>
              multiple
              options={locationTags || []}
              value={selectedLocationTags}
              onChange={(_, selected) =>
                handleLocationTagsChange(selected as Tag[])
              }
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) =>
                String(option.id) === String(value.id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('selectLocationTags')}
                  size="small"
                />
              )}
            />
          </Box>
        </Box>
      )}

      {availableFilters.includes('gender') && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            {t('gender')}
          </Typography>
          <ToggleButtonGroup
            value={genderValue}
            exclusive
            onChange={handleGenderChange}
            size="small"
            fullWidth
          >
            <ToggleButton value="all">{t('all')}</ToggleButton>
            <ToggleButton value="male">{t('male')}</ToggleButton>
            <ToggleButton value="female">{t('female')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {availableFilters.includes('age') && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            {t('age')}
          </Typography>
          <Slider
            value={[localFilters.ageMin || 1, localFilters.ageMax || 100]}
            onChange={handleAgeChange}
            valueLabelDisplay="auto"
            min={1}
            max={100}
          />
        </Box>
      )}

      {availableFilters.includes('age_group') && ageFilters.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TextField
            label={t('ageGroup')}
            size="small"
            value={
              ageFilters.find(
                (f) =>
                  f.minAge === localFilters.ageMin &&
                  f.maxAge === localFilters.ageMax
              )?.label || ''
            }
            onChange={(e) => handleAgeGroupChange(e.target.value)}
            options={[
              { label: t('all'), value: '' },
              ...ageFilters.map((f) => ({ label: f.label, value: f.label })),
            ]}
          />
        </Box>
      )}
    </Dialog>
  );
};
